// The one dialog that says a mission cannot go on without its tools — CCT-388.
//
// ⚠️ IT USED TO LIVE INSIDE `useMissionLaunch`, SO ONLY THE LAUNCH COULD RAISE IT. Every other
// moment a controller is found missing had to invent its own wording: the task screen ended up
// with a red line saying "reload the task, then try again" — an instruction that cannot work,
// for a condition retrying never changes. Two places saying the same refusal in two ways is one
// too many, and the weaker one is always the one the player meets.
//
// ⚠️ IT BLOCKS, AND ITS BUTTON LEADS WHERE ACTING IS POSSIBLE. A dialog that names a gesture
// ("activate them from the mission page") without leading there moves the work onto the reader:
// they must find that mission again, holding the instruction in their head.
import React, { useCallback, useState } from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useT } from '../i18n/useT';
import ServerMessage from './ServerMessage';

/** Natures the player can activate — anything else is named generically rather than guessed. */
const LIBELLE: Record<string, string> = { cloud: 'controllers.cloud', github: 'controllers.github' };

export function useControllersBlocked() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const navigation = useNavigation<any>();

  const [kinds, setKinds] = useState<string[] | null>(null);
  //: The mission the refusal is about — the dialog must be able to LEAD there, not merely name it.
  const [missionId, setMissionId] = useState<number | null>(null);

  /** Raise the blocking dialog for these controller natures. */
  const bloquer = useCallback((mission: number | null, natures: string[]) => {
    setMissionId(mission);
    setKinds(natures);
  }, []);

  const ouvrirLaMission = useCallback(() => {
    const id = missionId;
    setKinds(null);
    setMissionId(null);
    if (id) navigation.navigate('MissionDetail', { missionId: id });
  }, [missionId, navigation]);

  const modal = (
    <Modal visible={!!kinds} transparent animationType="fade" onRequestClose={() => setKinds(null)}>
      <View style={st.backdrop}>
        <View style={[st.card, { backgroundColor: colors.cardBackground }]}>
          <Text style={[st.title, { color: colors.textPrimary }]}>{t('controllers.blockedTitle')}</Text>
          {/* CCT-401 : la nature du message se VOIT. Ici elle est connue du client (un refus de
              contrôleurs est toujours un avertissement), donc elle est déclarée en dur plutôt
              que lue d'une réponse : l'inventer côté serveur pour un texte local serait une
              seconde source pour un fait qui n'en a qu'une. */}
          <ServerMessage type="warning" compact>{t('controllers.blockedBody')}</ServerMessage>
          {(kinds ?? []).map((k) => (
            <Text key={k} style={[st.body, { color: colors.warning }]}>
              {'• '}{LIBELLE[k] ? t(LIBELLE[k] as never) : k}
            </Text>
          ))}
          <TouchableOpacity onPress={ouvrirLaMission} style={[st.btn, { backgroundColor: colors.primary }]}>
            <Text style={st.btnText}>{t('controllers.blockedOpen')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  return { modal, bloquer, bloque: !!kinds };
}

const st = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { borderRadius: 16, padding: 22, width: '100%', maxWidth: 420 },
  title: { fontSize: 18, fontWeight: '700', marginBottom: 8 },
  body: { fontSize: 13, lineHeight: 19, marginBottom: 16 },
  btn: { marginTop: 16, borderRadius: 10, paddingVertical: 11, alignItems: 'center' },
  btnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
});
